import { ChangeDetectionStrategy, Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { PageChangeEvent, PaginatedSearchHeaderComponentModule, TokenPaginatorComponentModule } from '@shared/ui';

import { EDITOR_DIALOG_LARGE } from '../../editor-dialog-sizes';
import { PipelineEditorOutcome } from '../../../effects/pipeline-editor.effects';
import { PipelineData } from '../../../state/pipeline.state';
import { PipelineColumn, PipelineListItem, PipelinesListComponentModule } from '../pipelines-list.component';
import { PipelineEditorDialogComponent } from '../pipeline-editor/pipeline-editor-dialog.component';
import { MethodPipelineManagerFacade } from './method-pipeline-manager.facade';

/**
 * Smart component that stages assignment of pipelines to a method type. The left
 * list is the global pipeline catalog (search + pagination); the right list is
 * the pipelines assigned to the method type (pagination only, no search). Both
 * lists stay in sync through the shared assignment state. The NEW PIPELINE button
 * and the right-list edit icons open the pipeline editor dialog; on save the
 * produced draft is staged into the catalog and (for new pipelines) auto-assigned.
 *
 * Like the resource manager, this component declares no providers — the consumer
 * (or the Storybook host) supplies the state/effects/facade tree, the
 * `METHOD_PIPELINE_MANAGER_CONTEXT`, the shared `StagedPipelinesState` /
 * `AssignedPipelinesState` ledgers, and the `pipelineEditorSessionProviders`
 * tier the dialog resolves against (with `pipelineEditorCascadeProviders` — or
 * an equivalent label scope — reachable above it).
 */
@Component({
  selector: 'ts-method-pipeline-manager',
  templateUrl: './method-pipeline-manager.component.html',
  styleUrls: ['./method-pipeline-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MatDialogModule,
    PaginatedSearchHeaderComponentModule,
    TokenPaginatorComponentModule,
    PipelinesListComponentModule,
  ],
})
export class MethodPipelineManagerComponent implements OnInit, OnDestroy {
  public readonly leftColumns = [PipelineColumn.NAME, PipelineColumn.ASSIGNED];
  public readonly rightColumns = [PipelineColumn.NAME, PipelineColumn.EDIT, PipelineColumn.ASSIGNED];

  // Page size is locked at 10 (both lists).
  public readonly pageSizeOptions = [10];

  private readonly destroy$ = new Subject<void>();

  constructor(
    public readonly facade: MethodPipelineManagerFacade,
    private readonly dialog: MatDialog,
    private readonly injector: Injector
  ) {}

  ngOnInit(): void {
    this.facade.initialize();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public searchTermChanged(value: string): void {
    this.facade.search(value);
  }

  public handlePageEvent(event: PageChangeEvent<PipelineData>): void {
    this.facade.loadMore(event.nextToken);
  }

  public handleAssignedPageEvent(event: PageChangeEvent<PipelineData>): void {
    this.facade.loadMoreAssigned(event.nextToken);
  }

  public onToggleAvailable(item: PipelineListItem): void {
    this.facade.toggleAvailable(item);
  }

  public onUnassign(item: PipelineListItem): void {
    this.facade.unassign(item);
  }

  public onNewPipeline(): void {
    this.facade.beginCreatePipeline();
    this.openEditorDialog();
  }

  public onEditPipeline(item: PipelineListItem): void {
    this.facade.beginEditPipeline(item);
    this.openEditorDialog();
  }

  /**
   * UI control lives here (not in the facade): open the editor dialog with this
   * component's injector so the consumer's shared `PipelineEditorState` reaches
   * the dialog, and stage the produced draft on a `saved` outcome.
   */
  private openEditorDialog(): void {
    this.dialog
      .open<PipelineEditorDialogComponent, undefined, PipelineEditorOutcome | undefined>(PipelineEditorDialogComponent, {
        ...EDITOR_DIALOG_LARGE,
        hasBackdrop: true,
        injector: this.injector,
      })
      .afterClosed()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((outcome) => {
        if (outcome?.kind === 'saved') {
          this.facade.stageSavedPipeline();
        }
      });
  }
}
